// @workflow platform.viewer
/**
 * Where the 3D camera looks and what is picked: framing, selection, screenshots, coordinates.
 * Every operation forwards to today's implementation unchanged; the comment on each names it.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { instanceTreeOf } from './readiness.js';
import { tagOwnersIn } from './scene.js';

/** @param {object} ctx see core.js `createViewer` */
export function createCameraOps(ctx) {
  const { store, deps, current, service } = ctx;
  const hasModel = () => !!(current() && current().model);

  return {
    /**
     * forgeService.zoomToPoint3dViewer: a THREE.Box3 is framed as is; a point gets the legacy pad
     * of `1 / unitScale` viewer units around it (forgeService.js:4238, legacy unit debt).
     * @returns {boolean} false while there is no model
     */
    fitBox(target) {
      if (!hasModel() || !target) return false;
      service().zoomToPoint3dViewer(target);
      return true;
    },
    /** Frame the elements' world box (forgeUtils.getBBBox, then fitBox), without isolating them. */
    fitToDbIds(dbIds, model = null) {
      if (!hasModel() || !Array.isArray(dbIds) || !dbIds.length) return false;
      const box = deps.boxOf(dbIds, model);
      if (!box || Array.isArray(box)) return false;
      service().zoomToPoint3dViewer(box);
      return true;
    },
    /**
     * The 3D labels extension's createHighlightingTags (forgeService.getLabels3DExtension): the
     * blinking marker at each tag's point that "Zoom to tag in 3D" draws for its tag
     * (forgeService.zoomInAllAvailableViewers). It replaces the markers drawn before; tags without
     * a point get none. Not a viewer selection.
     * @param {object[]} tags facility tags (ForgeBaseTag and kin), as GET_BASE_TAGS_BY_IDS returns them
     * @returns {boolean} false while there is no viewer or labels extension
     */
    highlightTagsIn3d(tags) {
      if (!current() || !Array.isArray(tags) || !tags.length) return false;
      const ext = service().getLabels3DExtension();
      if (!ext) return false;
      ext.createHighlightingTags(tags);
      return true;
    },
    /** forgeService.worldToView3d: a world point in the model's viewer space, or `fallback`. */
    worldToView(point, fallback = null) {
      return service().worldToView3d(point, fallback);
    },
    /** mainExtension.selectDbIds: select the elements and fit the view to them. */
    select(dbIds) {
      if (!current()) return false;
      const ext = service().getMainExtension();
      if (!ext) return false;
      ext.selectDbIds(dbIds);
      return true;
    },
    /**
     * The selection as viewerHost.readSelection read it: the selected dbIds (store getter
     * `getSelectedDbids`), every tag with its element ids, and the tree's parent lookup, so a
     * caller can map elements back to the tags that own them.
     * @returns {{ dbIds: number[], owners: Array<{ id: *, dbIds: number[] }>, parentOf: (dbId: number) => number|null }}
     */
    selection() {
      const tree = instanceTreeOf(current());
      return {
        dbIds: store.getters.getSelectedDbids || [],
        owners: tagOwnersIn(store),
        parentOf: (dbId) => (tree ? tree.getNodeParentId(dbId) : null),
      };
    },
    /** Every tag in the store with its element ids (the tag id ↔ dbId mapping). */
    tagOwners() {
      return tagOwnersIn(store);
    },
    /** forgeService.takeScreenshot of the 3D canvas, as a base64 data URL; null without a viewer. */
    screenshot() {
      const viewer = current();
      if (!viewer) return Promise.resolve(null);
      return new Promise((resolve) => deps.takeScreenshot(viewer, resolve));
    },
    /**
     * Run `callback` once the model tree exists: now when the model finished loading, else once
     * on OBJECT_TREE_CREATED (the Task board's 3D isolation, Task/views/index.vue:1016).
     * @returns {boolean} false without a viewer
     */
    whenModelLoaded(callback) {
      const viewer = current();
      if (!viewer) return false;
      if (viewer.model && viewer.model.isLoadDone()) callback();
      else viewer.addEventListener(deps.events.OBJECT_TREE_CREATED, callback, { once: true });
      return true;
    },
  };
}
